import { useState, type SyntheticEvent } from "react";

import { useT, type Translate } from "../../../shared/i18n";
import { Button, Input, Segmented } from "../../../shared/ui";
import { RANGE_IDS, type LogFilters, type RangeId } from "./logFormat";

function rangeLabel(t: Translate, id: RangeId): string {
  switch (id) {
    case "15m":
      return t("admin.logs.range.15m");
    case "1h":
      return t("admin.logs.range.1h");
    case "6h":
      return t("admin.logs.range.6h");
    case "24h":
      return t("admin.logs.range.24h");
  }
}

/** User id, session id and a preset time range; applied on submit. */
export function TraceFilters({
  filters,
  onApply,
  hideUser = false,
}: {
  filters: LogFilters;
  onApply: (next: LogFilters) => void;
  /** The list is already scoped to one user (per-user Logs tab). */
  hideUser?: boolean;
}) {
  const t = useT();
  const [draft, setDraft] = useState(filters);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    onApply(draft);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {!hideUser && (
          <label className="flex min-w-48 flex-1 flex-col gap-1 text-footnote text-fg-muted">
            {t("admin.logs.filter.user")}
            <Input
              value={draft.userId}
              maxLength={64}
              onChange={(event) => {
                setDraft({ ...draft, userId: event.target.value });
              }}
            />
          </label>
        )}
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-footnote text-fg-muted">
          {t("admin.logs.filter.session")}
          <Input
            value={draft.sessionId}
            maxLength={64}
            onChange={(event) => {
              setDraft({ ...draft, sessionId: event.target.value });
            }}
          />
        </label>
      </div>
      <Segmented
        label={t("admin.logs.filter.range")}
        value={draft.range}
        onChange={(id) => {
          setDraft({ ...draft, range: id as RangeId });
        }}
        options={RANGE_IDS.map((id) => ({ id, label: rangeLabel(t, id) }))}
      />
      <div className="flex gap-2">
        <Button type="submit" variant="secondary">
          {t("admin.logs.filter.apply")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            const cleared = {
              ...draft,
              userId: hideUser ? draft.userId : "",
              sessionId: "",
            };
            setDraft(cleared);
            onApply(cleared);
          }}
        >
          {t("admin.logs.filter.clear")}
        </Button>
      </div>
    </form>
  );
}
